
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Search, Copy } from "lucide-react";
import { useAdminReferrals } from '@/hooks/useAdminAffiliate';
import { toast } from "sonner";
import { MobileReferralCard } from '@/components/admin/affiliate/MobileReferralCard';
import { useIsMobile } from '@/hooks/use-mobile';

export const ReferralTrackingPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const isMobile = useIsMobile();
  
  const { data: referralsResponse, isLoading } = useAdminReferrals(statusFilter);
  
  const referrals = referralsResponse?.data || [];

  const filteredReferrals = referrals.filter(referral => 
    referral.affiliateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    referral.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    referral.referralCode.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getBadgeColor = (status: string) => {
    switch (status) {
      case 'converted':
        return 'bg-green-100 text-green-800';
      case 'registered':
        return 'bg-blue-100 text-blue-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("User ID copied to clipboard");
  };
  
  const formatUserId = (userId: string) => {
    if (!userId || userId.includes("@")) return userId; // It's an email
    
    // Return shortened version with copy button
    return (
      <div className="flex items-center space-x-1">
        <span>{userId.substring(0, 8)}</span>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-5 w-5"
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(userId);
          }}
        >
          <Copy className="h-3.5 w-3.5" />
          <span className="sr-only">Copy</span>
        </Button>
      </div>
    );
  };

  if (isLoading) {
    return <div className="p-8">Loading referral data...</div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Referral Tracking</h1>
        <p className="text-muted-foreground mt-1">Monitor referral signups and conversions</p>
      </div>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Referrals</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search referrals..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="registered">Registered</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filteredReferrals.length > 0 ? (
            isMobile ? (
              // Mobile card layout
              <div className="space-y-3">
                {filteredReferrals.map((referral) => (
                  <MobileReferralCard
                    key={referral.id}
                    referral={referral}
                  />
                ))}
              </div>
            ) : (
              // Desktop table layout
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Affiliate</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Referral Code</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>IP Address</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredReferrals.map((referral) => (
                      <TableRow key={referral.id}>
                        <TableCell>{new Date(referral.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>{referral.affiliateName}</TableCell>
                        <TableCell>{formatUserId(referral.userName)}</TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-1 py-0.5 rounded">
                            {referral.referralCode}
                          </code>
                        </TableCell>
                        <TableCell>
                          <Badge className={getBadgeColor(referral.status)}>
                            {referral.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{referral.ipAddress}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              No referrals found matching your criteria
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralTrackingPage;
